import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Search, LogOut, Sun, Moon, MessageCircle, Home, Users, Settings, Shield, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import NotificationsDropdown from "@/components/NotificationsDropdown";
import { useUI } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { openChat } = useUI();
  const [dark, setDark] = useState(!document.documentElement.classList.contains("light"));
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const toggleTheme = () => {
    const nowLight = document.documentElement.classList.toggle("light");
    localStorage.setItem("xealth-theme", nowLight ? "light" : "dark");
    setDark(!nowLight);
  };

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        setResults(data);
        setShowResults(true);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 glass bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <button data-testid="logo-btn" onClick={() => navigate("/")} className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6C0BA9] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#6C0BA9]/40">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl font-extrabold hidden sm:block">xealth</span>
        </button>

        <div ref={searchRef} className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q && setShowResults(true)}
            placeholder="Search Xealth"
            className="pl-10 h-10 rounded-full bg-secondary border-transparent focus-visible:border-[#6C0BA9]"
          />
          {showResults && results.length > 0 && (
            <div className="absolute mt-2 w-full bg-popover border border-border rounded-2xl shadow-2xl overflow-hidden z-50">
              {results.map((u) => (
                <button
                  key={u.user_id}
                  data-testid={`search-result-${u.user_id}`}
                  onClick={() => { navigate(`/profile/${u.user_id}`); setShowResults(false); setQ(""); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent transition-colors text-left"
                >
                  <Avatar user={u} size={34} />
                  <span className="text-sm font-medium flex items-center gap-1">{u.name}{u.verified && <VerifiedBadge size={13} />}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 ml-auto">
          <button data-testid="nav-home-btn" onClick={() => navigate("/")} className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center hover:bg-accent transition-colors">
            <Home className="w-5 h-5" />
          </button>
          <button data-testid="nav-friends-btn" onClick={() => navigate("/friends")} className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center hover:bg-accent transition-colors">
            <Users className="w-5 h-5" />
          </button>
          <button data-testid="chat-toggle-btn" onClick={() => openChat({})} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent transition-colors">
            <MessageCircle className="w-5 h-5" />
          </button>
          <NotificationsDropdown />
          <button data-testid="theme-toggle-btn" onClick={toggleTheme} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent transition-colors">
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="profile-menu-btn" className="ml-1"><Avatar user={user} size={38} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
              <div className="px-2 py-2">
                <div className="flex items-center gap-2 text-sm font-semibold">{user?.name}{user?.verified && <VerifiedBadge size={14} />}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/profile/${user.user_id}`)} className="cursor-pointer">View Profile</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")} className="cursor-pointer"><Settings className="w-4 h-4 mr-2" />Settings & verification</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-guidelines" onClick={() => navigate("/guidelines")} className="cursor-pointer"><ShieldCheck className="w-4 h-4 mr-2" />Community Guidelines</DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer text-[#C084FC]"><Shield className="w-4 h-4 mr-2" />Moderation dashboard</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-red-400"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
